/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// RetailProductFinderPage — the back office for the retail locations behind the
// public "where to buy" store finder.
//
// Ported from the source app, which had three tabs. Two changed:
//
//   Locations   — ported in full. The working core.
//   Data Source — NOT ported. The source synced stores from an Excel workbook in
//                 OneDrive using Microsoft Graph delegated + app-only tokens.
//                 Brand Command has no Azure AD, so that tab could only ever
//                 have been dead UI. Its job now belongs to System Settings →
//                 Sync Settings, which connects a Google Sheet instead.
//   Widget      — PORTED 2026-08-04. It was deferred at port time because it
//                 configured a widget that did not exist here yet; that stopped
//                 being true when embed/dist/retail-finder.js shipped, so the
//                 stub became a lie about the product. Now live, with two
//                 additions the single-tenant source had no need for: the embed
//                 key, and the allowed-websites list.
import React, { useState, useRef } from "react";
import LocationsTab from "./LocationsTab.jsx";
import WidgetConfigTab from "./WidgetConfigTab.jsx";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import "./RetailProductFinderPage.css";

const TABS = [
  { id: "locations", label: "Locations" },
  { id: "widget",    label: "Website Widget" },
];

export default function RetailProductFinderPage({ canEdit = true }) {
  const [tab, setTab] = useState("locations");
  const tabRefs = useRef({});

  // Backlog item 75 follow-up. These were bare buttons: they LOOKED like tabs
  // and announced as a row of unrelated buttons. The roles below are only half
  // the contract — the APG tab pattern also promises that arrows move between
  // tabs and that Tab itself jumps out to the panel, which is why this is
  // roving tabindex and not just a set of attributes.
  const onTabKeyDown = (e) => {
    const i = TABS.findIndex(t => t.id === tab);
    let next = null;
    if (e.key === "ArrowRight") next = TABS[(i + 1) % TABS.length];
    else if (e.key === "ArrowLeft") next = TABS[(i - 1 + TABS.length) % TABS.length];
    else if (e.key === "Home") next = TABS[0];
    else if (e.key === "End") next = TABS[TABS.length - 1];
    if (!next) return;
    e.preventDefault();          // stop the scroll container moving under us
    setTab(next.id);
    tabRefs.current[next.id]?.focus();
  };

  return (
    <div className="rpf-page">
      <div className="rpf-topbar">
        <HomeButton />
        <h1>Retail Product Finder</h1>
        <span className="rpf-crumb">Locations on the website store-finder map</span>
        <div className="rpf-spacer" />
      </div>

      <div className="rpf-tabs" role="tablist" aria-label="Retail Product Finder sections">
        {TABS.map(t => (
          <button
            key={t.id}
            ref={el => { tabRefs.current[t.id] = el; }}
            type="button"
            role="tab"
            id={`rpf-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`rpf-panel-${t.id}`}
            // Roving tabindex: only the selected tab is in the tab order, so Tab
            // leaves the strip for the panel instead of walking every tab first.
            tabIndex={tab === t.id ? 0 : -1}
            className={`rpf-tab${tab === t.id ? " is-active" : ""}`}
            onClick={() => setTab(t.id)}
            onKeyDown={onTabKeyDown}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* One panel per tab, each owned by its tab. Only the selected one is
          rendered — an inactive panel would otherwise keep mounting a screen
          nobody asked for, and both tabs load their own data.
          No tabindex on the panels: APG only asks for that when a panel has no
          focusable content of its own, and both of these are full of controls.
          Adding it would put a focus ring round the whole screen for no gain. */}
      <div className="rpf-body">
        {tab === "locations" && (
          <div role="tabpanel" id="rpf-panel-locations" aria-labelledby="rpf-tab-locations">
            <LocationsTab canEdit={canEdit} />
          </div>
        )}

        {tab === "widget" && (
          <div role="tabpanel" id="rpf-panel-widget" aria-labelledby="rpf-tab-widget">
            <WidgetConfigTab canEdit={canEdit} />
          </div>
        )}
      </div>
    </div>
  );
}
